import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Seo from '../components/Seo';
import Footer from '../components/Footer';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = 'Login failed. Please try again.';
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail)) {
        errorMsg = detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
      }
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
    // `auth.emergentagent.com` IS the documented Emergent-managed Google OAuth proxy — it's
    // intentional and correct; do not "replace it with window.location.origin" because there
    // is no /oauth route here. The redirect URL must use window.location.origin (no fallbacks).
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex">
        <Seo
          title="Sign in — Forge Ledger"
          description="Sign in to Forge Ledger to manage your budgets, accounts and future balance forecasts. UK GDPR compliant, encrypted at rest."
          path="/login"
        />
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-4xl tracking-tighter font-extrabold text-[#020617] mb-2" data-testid="login-title">
              Welcome Back
            </h1>
            <p className="text-base text-[#64748B]">Sign in to manage your finances</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-6" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-[#020617]">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                placeholder="you@example.com"
                data-testid="login-email-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-[#020617]">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-[#0052FF] hover:text-[#003ECC] font-medium"
                  data-testid="login-forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 bg-white border border-[#E2E8F0] rounded-lg px-4 py-2 text-[#020617] focus:border-[#0052FF] focus:ring-1 focus:ring-[#0052FF]"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0052FF] text-white rounded-lg px-6 py-2.5 font-medium hover:bg-[#003ECC] transition-colors"
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-[#E2E8F0]"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-[#F8FAFC] text-[#64748B]">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleGoogleLogin}
              className="w-full mt-4 bg-white border border-[#E2E8F0] text-[#020617] rounded-lg px-6 py-2.5 font-medium hover:bg-[#F8FAFC] transition-colors"
              data-testid="google-login-button"
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </Button>
          </div>

          <p className="mt-6 text-center text-sm text-[#64748B]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#0052FF] font-medium hover:text-[#003ECC]">
              Sign up
            </Link>
          </p>
          <p className="mt-3 text-center text-xs text-[#94A3B8]">
            <Link to="/security" className="hover:text-[#0052FF]" data-testid="login-security-link">
              How we keep your data safe →
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#0052FF] to-[#003ECC] items-center justify-center p-12">
        <div className="max-w-md text-white">
          <h2 className="text-3xl font-bold mb-4">Take Control of Your Finances</h2>
          <p className="text-lg opacity-90">
            Track your spending, manage your accounts, and predict your future balance with precision.
          </p>
        </div>
      </div>
      </div>
      <Footer variant="full" />
    </div>
  );
};

export default Login;
